import React, { Component } from "react";
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import Modal from "reactstrap/lib/Modal";
import ModalBody from "reactstrap/lib/ModalBody";
import { Icon, TablePagination } from "@material-ui/core";
import moment from "moment";
import ApiEndpoints from "../../../constants/api_endpoints";
import store from "../../../store";
import { setError, setLoader, setSuccess } from "../../../actions/action-creators";
import ConfirmAction from "../../global/confirm-action";
import InputFeild from "../../global/form/input";
import PartnerDetail from "../user_profile";

const STATUS = {
    0: { label: "Pending",  bg: "#fff7e6", color: "#d46b08" },
    1: { label: "Approved", bg: "#e6f7ee", color: "#1a7a42" },
    2: { label: "Rejected", bg: "#fff0f0", color: "#c0392b" },
};
const STATUS_FILTERS = [
    { value: "0", label: "Pending" },
    { value: "1", label: "Approved" },
    { value: "2", label: "Rejected" },
    { value: "",  label: "All" },
];

const fmtDate = (v) => { const m = moment(v); return (v && m.isValid()) ? m.utcOffset("+05:30").format("Do MMM'YY") : "—"; };
const fmtINR  = (v) => v != null ? "₹" + Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—";

class UserInvoice extends Component {
    constructor(props) {
        super(props);
        this.state = {
            invoiceList: [],
            status: "0",
            searchText: "",
            currentPage: 0,
            rowsPerPage: 20,
            isConfirm: false,
            isConfirmMessage: null,
            isReject: false, // invoice being rejected
            rejectReason: "",
            isOpenProfile: false,
            approveForm: false, // { invoice, payouts, gstin, has_verified_gst, payout_data_ids, invoice_number, invoice_date }
        };
    }

    componentDidMount() {
        this.fetchInvoices();
    }

    fetchInvoices = async () => {
        store.dispatch(setLoader(true));
        try {
            const params = this.state.status !== "" ? { status: this.state.status } : {};
            const resp = await ApiEndpoints.UserInvoice.fetch(params);
            this.setState({ invoiceList: resp.data.data || [], currentPage: 0 });
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    viewInvoice = async (file_id) => {
        store.dispatch(setLoader(true));
        try {
            const resp = await ApiEndpoints.Files.getFileID({ file_id });
            const url = resp.data.data[0] ? resp.data.data[0].url : null;
            if (!url) throw new Error("File not found");
            window.open(url, "_blank");
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    // invoices received by email need invoice number/date/payout from the admin before approval
    openApproveForm = async (invoice) => {
        store.dispatch(setLoader(true));
        try {
            const resp = await ApiEndpoints.UserInvoice.approveOptions({ user_invoice_id: invoice.user_invoice_id });
            const data = resp.data.data || {};
            const payouts = data.payouts || [];
            const invoicePayoutIds = (invoice.payouts || []).map(p => p.payout_data_id);
            // invoice's own payouts, or (email invoice with one candidate) the only uninvoiced payout
            const candidates = payouts.filter(p => !p.approved_invoice_number);
            const defaultPayouts = invoicePayoutIds.length ? invoicePayoutIds : candidates.length === 1 ? [candidates[0].payout_data_id] : [];
            this.setState({
                approveForm: {
                    ...data,
                    invoice,
                    payout_data_ids: defaultPayouts,
                    invoice_number: invoice.invoice_number || "",
                    invoice_date: invoice.invoice_date || "",
                },
            });
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    submitApproveForm = async (e) => {
        e.preventDefault();
        const { approveForm } = this.state;
        const selected = approveForm.payouts.filter(p => approveForm.payout_data_ids.includes(p.payout_data_id));
        if (!selected.length) return store.dispatch(setError({ show: true, message: "Select the payout(s) this invoice is for" }));
        const gst = selected.reduce((sum, p) => sum + Number(p.gst_amount), 0);
        if (!window.confirm(`Approve and credit ${fmtINR(gst)} GST to the partner's wallet?`)) return;
        await this.approveInvoice(approveForm.invoice, {
            payout_data_ids: approveForm.payout_data_ids,
            invoice_number: approveForm.invoice_number.trim(),
            invoice_date: approveForm.invoice_date,
        });
    };

    approveInvoice = async (invoice, details = {}) => {
        store.dispatch(setLoader(true));
        try {
            const resp = await ApiEndpoints.UserInvoice.approve({ user_invoice_id: invoice.user_invoice_id, ...details });
            this.setState({ approveForm: false });
            store.dispatch(setSuccess({ show: true, message: resp.data.message }));
            await this.fetchInvoices();
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    rejectInvoice = async (e) => {
        e.preventDefault();
        const { isReject, rejectReason } = this.state;
        if (!rejectReason.trim()) return store.dispatch(setError({ show: true, message: "Please enter the reject reason" }));
        store.dispatch(setLoader(true));
        try {
            const resp = await ApiEndpoints.UserInvoice.reject({ user_invoice_id: isReject.user_invoice_id, reject_reason: rejectReason.trim() });
            store.dispatch(setSuccess({ show: true, message: resp.data.message }));
            this.setState({ isReject: false, rejectReason: "" });
            await this.fetchInvoices();
        } catch (err) {
            store.dispatch(setError({ show: true, message: err.response ? err.response.data.message : err.toString() }));
        }
        store.dispatch(setLoader(false));
    };

    render() {
        const { invoiceList, status, searchText, currentPage, rowsPerPage, isReject, rejectReason, isOpenProfile, approveForm } = this.state;
        const search = searchText.trim().toLowerCase();
        const filtered = !search ? invoiceList : invoiceList.filter(inv => {
            const user = inv.User || {};
            return [user.first_name, user.mobile, user.id && "pmw" + user.id, inv.invoice_number, inv.gstin]
                .filter(Boolean)
                .some(v => String(v).toLowerCase().includes(search));
        });
        const page = filtered.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

        return (
            <div>
                <div style={S.header}>
                    <h4 className="m-0">User Invoices</h4>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <div style={S.segment}>
                            {STATUS_FILTERS.map(f => (
                                <span
                                    key={f.value}
                                    style={{ ...S.segmentItem, ...(status === f.value ? S.segmentActive : {}) }}
                                    onClick={() => this.setState({ status: f.value }, this.fetchInvoices)}
                                >
                                    {f.label}
                                </span>
                            ))}
                        </div>
                        <InputFeild
                            style={{ fontSize: 13, margin: 0, width: 240 }}
                            icon="search"
                            placeholder="Search name, PMW id, mobile, invoice no"
                            value={searchText}
                            onChange={(e) => this.setState({ searchText: e.target.value, currentPage: 0 })}
                        />
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div style={S.emptyState}>No invoices found.</div>
                ) : (
                    <>
                        <div style={S.tableWrapper}>
                            <table style={S.table}>
                                <thead>
                                    <tr style={S.tHead}>
                                        <th style={S.th}>Partner</th>
                                        <th style={S.th}>Payout</th>
                                        <th style={S.th}>Payout Amount</th>
                                        <th style={S.th}>Taxable</th>
                                        <th style={S.th}>GST</th>
                                        <th style={S.th}>Invoice</th>
                                        <th style={S.th}>GSTIN</th>
                                        <th style={S.th}>Uploaded</th>
                                        <th style={S.th}>Status</th>
                                        <th style={S.th}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {page.map(inv => {
                                        const user = inv.User || {};
                                        const payouts = inv.payouts || [];
                                        const statusCfg = STATUS[inv.status] || STATUS[0];
                                        return (
                                            <tr key={inv.user_invoice_id} style={S.tRow}>
                                                <td style={S.td}>
                                                    <span style={{ fontWeight: 700, color: "#1a1a1a", cursor: "pointer" }} onClick={() => this.setState({ isOpenProfile: user })}>
                                                        {user.first_name || "—"}
                                                    </span>
                                                    <div style={S.sub}>{user.id ? "PMW" + user.id : inv.user_id}</div>
                                                </td>
                                                <td style={S.td}>
                                                    {payouts.length ? payouts.map(p => (
                                                        <div key={p.payout_data_id}>
                                                            {p.payout_title || "#" + p.payout_data_id}
                                                            <span style={S.sub}> · {fmtDate(p.created_on)}</span>
                                                        </div>
                                                    )) : <span style={{ color: "#aaa" }}>Select on approval</span>}
                                                </td>
                                                <td style={S.td}>{payouts.length ? fmtINR(payouts.reduce((sum, p) => sum + Number(p.amount || 0), 0)) : "—"}</td>
                                                <td style={S.td}>{inv.taxable_amount != null ? fmtINR(inv.taxable_amount) : "—"}</td>
                                                <td style={{ ...S.td, fontWeight: 700 }}>
                                                    {inv.gst_amount != null ? fmtINR(inv.gst_amount) : "—"}
                                                    <div style={S.sub}>@ {Number(inv.gst_rate)}%</div>
                                                </td>
                                                <td style={S.td}>
                                                    {inv.invoice_number || <span style={{ color: "#aaa" }}>To be filled</span>}
                                                    {!!inv.invoice_date && <div style={S.sub}>{fmtDate(inv.invoice_date)}</div>}
                                                    {inv.source === "EMAIL" && (
                                                        <div style={S.sub}>
                                                            <span style={{ ...S.pill, background: "#e8f1ff", color: "#1677ff", padding: "1px 8px" }}>via Email</span>
                                                            {inv.source_email ? " " + inv.source_email : ""}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12 }}>{inv.gstin || "—"}</td>
                                                <td style={{ ...S.td, whiteSpace: "nowrap" }}>{fmtDate(inv.created_on)}</td>
                                                <td style={S.td}>
                                                    <span style={{ ...S.pill, background: statusCfg.bg, color: statusCfg.color }}>{statusCfg.label}</span>
                                                    {inv.status === 2 && inv.reject_reason && <div style={S.sub}>{inv.reject_reason}</div>}
                                                    {inv.status !== 0 && <div style={S.sub}>{fmtDate(inv.reviewed_on)}</div>}
                                                </td>
                                                <td style={S.td}>
                                                    <UncontrolledDropdown>
                                                        <DropdownToggle tag="span" style={{ cursor: "pointer" }}>
                                                            <Icon style={{ fontSize: 20, color: "#555" }}>more_vert</Icon>
                                                        </DropdownToggle>
                                                        <DropdownMenu right>
                                                            <DropdownItem style={{ fontSize: 14 }} onClick={() => this.viewInvoice(inv.file_id)}>View Invoice</DropdownItem>
                                                            {inv.status === 0 && (
                                                                <DropdownItem
                                                                    style={{ fontSize: 14 }}
                                                                    onClick={() => inv.source === "EMAIL" ? this.openApproveForm(inv) : this.setState({
                                                                        isConfirm: () => this.approveInvoice(inv),
                                                                        isConfirmMessage: `Approve and credit ${fmtINR(inv.gst_amount)} GST to ${user.first_name || "partner"}'s wallet?`,
                                                                    })}
                                                                >
                                                                    Approve
                                                                </DropdownItem>
                                                            )}
                                                            {inv.status === 0 && (
                                                                <DropdownItem style={{ fontSize: 14 }} onClick={() => this.setState({ isReject: inv, rejectReason: "" })}>Reject</DropdownItem>
                                                            )}
                                                        </DropdownMenu>
                                                    </UncontrolledDropdown>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <TablePagination
                            rowsPerPageOptions={[20, 50, 100]}
                            component="div"
                            count={filtered.length}
                            rowsPerPage={rowsPerPage}
                            page={currentPage}
                            onPageChange={(_, p) => this.setState({ currentPage: p })}
                            onRowsPerPageChange={(e) => this.setState({ rowsPerPage: Number(e.target.value), currentPage: 0 })}
                        />
                    </>
                )}

                <ConfirmAction
                    message={this.state.isConfirmMessage}
                    isOpen={!!this.state.isConfirm}
                    onClose={() => this.setState({ isConfirm: false, isConfirmMessage: null })}
                    callback={this.state.isConfirm}
                />

                {!!isReject && (
                    <Modal isOpen={true} className="modal-dialog-centered" toggle={() => this.setState({ isReject: false })}>
                        <ModalBody>
                            <h5 className="mb-1">Reject Invoice {isReject.invoice_number}</h5>
                            <p className="text-muted" style={{ fontSize: 13 }}>The partner will be emailed this reason and asked to re-upload.</p>
                            <form onSubmit={this.rejectInvoice}>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    maxLength={255}
                                    required
                                    placeholder="Reject reason (e.g. GSTIN mismatch, wrong amount)"
                                    value={rejectReason}
                                    onChange={(e) => this.setState({ rejectReason: e.target.value })}
                                />
                                <div className="text-right mt-3">
                                    <Button type="button" color="link" onClick={() => this.setState({ isReject: false })}>Cancel</Button>
                                    <Button type="submit" color="danger">Reject</Button>
                                </div>
                            </form>
                        </ModalBody>
                    </Modal>
                )}

                {!!approveForm && (
                    <Modal isOpen={true} className="modal-dialog-centered" size="lg" toggle={() => this.setState({ approveForm: false })}>
                        <ModalBody>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <h5 className="m-0">Approve Invoice</h5>
                                <Button size="sm" color="link" onClick={() => this.viewInvoice(approveForm.invoice.file_id)}>View invoice file</Button>
                            </div>
                            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                                Received by email{approveForm.invoice.source_email ? " from " + approveForm.invoice.source_email : ""}. Fill the details from the invoice file.
                                {" "}GSTIN on record: <b>{approveForm.gstin || "—"}</b>
                            </p>
                            {!approveForm.has_verified_gst && !approveForm.invoice.gstin && (
                                <p style={{ fontSize: 13, color: "#c0392b" }}>⚠ This partner has no verified GST document — verify it in their profile before approving.</p>
                            )}
                            <form onSubmit={this.submitApproveForm}>
                                <label style={S.label}>Payouts covered by this invoice</label>
                                <div style={S.payoutPicker}>
                                    {approveForm.payouts.map(p => {
                                        const checked = approveForm.payout_data_ids.includes(p.payout_data_id);
                                        return (
                                            <label key={p.payout_data_id} style={{ ...S.payoutOption, opacity: p.approved_invoice_number ? 0.5 : 1 }}>
                                                <input
                                                    type="checkbox"
                                                    disabled={!!p.approved_invoice_number}
                                                    checked={checked}
                                                    onChange={() => this.setState({
                                                        approveForm: {
                                                            ...approveForm,
                                                            payout_data_ids: checked
                                                                ? approveForm.payout_data_ids.filter(id => id !== p.payout_data_id)
                                                                : [...approveForm.payout_data_ids, p.payout_data_id],
                                                        },
                                                    })}
                                                />
                                                <span>
                                                    {(p.payout_title || "#" + p.payout_data_id)} · {fmtDate(p.created_on)} · Taxable {fmtINR(p.taxable_amount)} · GST {fmtINR(p.gst_amount)}
                                                    {p.approved_invoice_number ? ` · GST already credited (${p.approved_invoice_number})` : ""}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                                <div style={{ display: "flex", gap: 12 }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={S.label}>Invoice Number</label>
                                        <input
                                            className="form-control"
                                            required
                                            maxLength={50}
                                            value={approveForm.invoice_number}
                                            onChange={(e) => this.setState({ approveForm: { ...approveForm, invoice_number: e.target.value } })}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={S.label}>Invoice Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            required
                                            max={moment().format("YYYY-MM-DD")}
                                            value={approveForm.invoice_date}
                                            onChange={(e) => this.setState({ approveForm: { ...approveForm, invoice_date: e.target.value } })}
                                        />
                                    </div>
                                </div>
                                <div className="text-right mt-3">
                                    <Button type="button" color="link" onClick={() => this.setState({ approveForm: false })}>Cancel</Button>
                                    <Button type="submit" color="success">Approve & Credit GST</Button>
                                </div>
                            </form>
                        </ModalBody>
                    </Modal>
                )}

                {!!isOpenProfile && <PartnerDetail {...isOpenProfile} toggle={() => this.setState({ isOpenProfile: false })} />}
            </div>
        );
    }
}

const S = {
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 },
    segment: { display: "inline-flex", border: "1px solid #e0e0e0", borderRadius: 999, overflow: "hidden", background: "#fff" },
    segmentItem: { padding: "4px 14px", fontSize: 13, color: "#555", cursor: "pointer", userSelect: "none" },
    segmentActive: { background: "#1a1a1a", color: "#fff", fontWeight: 700 },
    tableWrapper: { border: "1px solid #e8e8e8", borderRadius: 10, overflow: "auto", background: "#fff" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
    tHead: { background: "#f5f5f5" },
    th: { padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#555", borderBottom: "1px solid #e8e8e8", whiteSpace: "nowrap" },
    tRow: { borderBottom: "1px solid #f0f0f0" },
    td: { padding: "11px 14px", color: "#333", verticalAlign: "top" },
    sub: { fontSize: 11, color: "#888", marginTop: 2 },
    label: { fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 },
    payoutPicker: { border: "1px solid #e0e0e0", borderRadius: 6, maxHeight: 200, overflowY: "auto", marginBottom: 16 },
    payoutOption: { display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 12px", margin: 0, fontSize: 13, borderBottom: "1px solid #f3f3f3", cursor: "pointer" },
    pill: { borderRadius: 999, padding: "3px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" },
    emptyState: { padding: "40px 20px", textAlign: "center", color: "#aaa", fontSize: 14, border: "1px solid #e8e8e8", borderRadius: 10, background: "#fff" },
};

export default UserInvoice;
